<template>
  <Transition
    enter-active-class="transition ease-out duration-300"
    enter-from-class="translate-y-2 opacity-0"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition ease-in duration-200"
    leave-from-class="translate-y-0 opacity-100"
    leave-to-class="translate-y-2 opacity-0"
  >
    <div
      v-if="visible"
      class="fixed top-4 right-4 z-50 w-96 bg-white rounded-lg shadow-xl border-l-4 border-orange-500"
    >
      <div class="p-4">
        <!-- Header -->
        <div class="flex items-start justify-between mb-2">
          <div class="flex items-center space-x-2">
            <div class="flex-shrink-0">
              <svg class="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-gray-900">Price Alert Triggered</h3>
          </div>
          <button
            @click="dismiss"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Alert Details -->
        <div class="space-y-2">
          <div class="flex items-baseline space-x-2">
            <span class="text-2xl font-bold text-gray-900">{{ triggeredAlert.alert.symbol }}</span>
            <span v-if="triggeredAlert.alert.symbolName" class="text-sm text-gray-600">
              {{ triggeredAlert.alert.symbolName }}
            </span>
          </div>

          <div class="flex items-baseline space-x-3">
            <span class="text-xl font-semibold text-gray-900">
              ${{ triggeredAlert.currentPrice.toFixed(2) }}
            </span>
            <span
              :class="[
                'text-sm font-medium',
                triggeredAlert.changePercent >= 0 ? 'text-green-600' : 'text-red-600'
              ]"
            >
              {{ triggeredAlert.changePercent >= 0 ? '+' : '' }}{{ triggeredAlert.changePercent.toFixed(2) }}%
            </span>
          </div>

          <div class="text-sm text-gray-600">
            <span class="font-medium">Target:</span>
            <span class="ml-1">${{ triggeredAlert.alert.targetPrice.toFixed(2) }}</span>
            <span class="ml-1 text-gray-500">
              ({{ triggeredAlert.alert.condition === 'ABOVE' ? 'Above' : 'Below' }})
            </span>
          </div>

          <div v-if="triggeredAlert.alert.note" class="text-sm text-gray-600 pt-1 border-t border-gray-200">
            <span class="font-medium">Note:</span>
            <span class="ml-1">{{ triggeredAlert.alert.note }}</span>
          </div>
        </div>

        <!-- Actions -->
        <div class="mt-4 flex space-x-2">
          <!-- Buy action button if action is BUY -->
          <button
            v-if="triggeredAlert.alert.action === 'BUY'"
            @click="handleBuy"
            class="flex-1 px-3 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors"
          >
            <span class="flex items-center justify-center">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Buy {{ triggeredAlert.alert.symbol }}
            </span>
          </button>
          
          <!-- Sell action button if action is SELL -->
          <button
            v-if="triggeredAlert.alert.action === 'SELL'"
            @click="handleSell"
            class="flex-1 px-3 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors"
          >
            <span class="flex items-center justify-center">
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
              </svg>
              Sell {{ triggeredAlert.alert.symbol }}
            </span>
          </button>

          <!-- View Stock button if no action or as additional button -->
          <button
            v-if="!triggeredAlert.alert.action || triggeredAlert.alert.action === 'NONE'"
            @click="viewStock"
            class="flex-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            View Stock
          </button>
          
          <button
            @click="dismiss"
            :class="[
              'px-3 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors',
              triggeredAlert.alert.action && triggeredAlert.alert.action !== 'NONE' ? 'flex-shrink-0' : 'flex-1'
            ]"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { logger } from '@/utils/logger'
import type { TriggeredAlert } from '../../../shared/src/types'

const props = defineProps<{
  triggeredAlert: TriggeredAlert
  autoHideDuration?: number // milliseconds, 0 or undefined = no auto hide
}>()

const emit = defineEmits<{
  dismiss: []
  buy: [symbol: string, price: number]
  sell: [symbol: string]
}>()

const visible = ref(false)

onMounted(() => {
  // Debug: Log the triggered alert data
  logger.debug('AlertNotification mounted with alert:', props.triggeredAlert)
  logger.debug('Alert action value:', props.triggeredAlert.alert.action)
  logger.debug('Action comparison - BUY:', props.triggeredAlert.alert.action === 'BUY')
  logger.debug('Action comparison - SELL:', props.triggeredAlert.alert.action === 'SELL')
  logger.debug('Action comparison - NONE:', props.triggeredAlert.alert.action === 'NONE')
  
  // Fade in animation
  setTimeout(() => {
    visible.value = true
  }, 100)

  // Auto hide if duration specified
  if (props.autoHideDuration && props.autoHideDuration > 0) {
    setTimeout(() => {
      dismiss()
    }, props.autoHideDuration)
  }
})

const dismiss = () => {
  visible.value = false
  setTimeout(() => {
    emit('dismiss')
  }, 200) // Wait for fade out animation
}

const handleBuy = () => {
  emit('buy', props.triggeredAlert.alert.symbol, props.triggeredAlert.currentPrice)
  dismiss()
}

const handleSell = () => {
  emit('sell', props.triggeredAlert.alert.symbol)
  dismiss()
}

const viewStock = () => {
  window.open(`/stocks/${props.triggeredAlert.alert.symbol}`, '_blank')
  dismiss()
}
</script>
